import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../utils/axios-instance";
import { auth } from "../../helper/firebaseClient";
import useAuth from "../../stores/authStore";
import { useDarkMode } from "../../context/darkModeContext";
import toast, { Toaster } from "react-hot-toast";
import AutopayOptions, {
  DEFAULT_AUTOPAY_VALUE,
  validateAutopaySelection,
  bankPayload,
} from "../autopay/AutopayOptions";
import {
  fetchAutopayStatus,
  setupAutopayMandate,
  cancelAutopay,
  apiError,
} from "../../utils/autopay";

const Subscription = () => {
  const profileData = useAuth((state) => state.profileData);
  const navigate = useNavigate();
  const { darkMode } = useDarkMode();
  const [subscription, setSubscription] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  // ── Change plan (upgrade/downgrade) ─────────────────────────────────────
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [selectedSeats, setSelectedSeats] = useState(1);
  const [changeQuote, setChangeQuote] = useState(null);
  const [changeQuoteLoading, setChangeQuoteLoading] = useState(false);
  const [changeQuoteError, setChangeQuoteError] = useState(null);
  const [applying, setApplying] = useState(false);

  // ── AutoPay ──────────────────────────────────────────────────────────────
  const [autopay, setAutopay] = useState(null);
  const [autopayModal, setAutopayModal] = useState(false);
  const [autopayValue, setAutopayValue] = useState({
    ...DEFAULT_AUTOPAY_VALUE,
    mode: "autopay",
  });
  const [autopayBusy, setAutopayBusy] = useState(false);

  useEffect(() => {
    if (!profileData?.org_id) return;
    fetchData();
    fetchPlans();
  }, [profileData?.org_id]);

  // Default the plan-change picker to the org's current plan/seats once
  // subscription data has loaded.
  useEffect(() => {
    if (!subscription?.planCode) return;
    setSelectedPlan((prev) => prev || subscription.planCode);
    setSelectedSeats((prev) =>
      prev && prev !== 1 ? prev : subscription?.seats?.paid || 1,
    );
  }, [subscription]);

  // Preview the prorated amount whenever the picker changes. Debounced
  // slightly isn't necessary here since it's a select + number input, not
  // free typing.
  useEffect(() => {
    if (!subscription?.billingCycle || !selectedPlan || !selectedSeats) return;
    if (
      selectedPlan === subscription.planCode &&
      Number(selectedSeats) === subscription?.seats?.paid
    ) {
      setChangeQuote(null);
      setChangeQuoteError(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setChangeQuoteLoading(true);
      setChangeQuoteError(null);
      try {
        const { data } = await api.post("/payments/quote/", {
          plan_code: selectedPlan,
          billing_cycle: subscription.billingCycle,
          seats: Number(selectedSeats),
        });
        if (!cancelled) setChangeQuote(data);
      } catch (err) {
        if (!cancelled) {
          setChangeQuoteError(
            err.response?.data?.error || "Couldn't calculate this change.",
          );
          setChangeQuote(null);
        }
      } finally {
        if (!cancelled) setChangeQuoteLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedPlan, selectedSeats, subscription]);

  const fetchPlans = async () => {
    try {
      const res = await api.get(`/payments/plans/`);
      setPlans(res.data || []);
    } catch (err) {
      console.error("Failed to load plans", err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      const subRes = await api.get(`/payments/current/`);
      const historyRes = await api.get(`/payments/history/`);

      setSubscription(subRes.data);
      setHistory(historyRes.data || []);
      loadAutopay();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const isOrgAdmin = profileData?.role_priv === "org_admin";

  const loadAutopay = async () => {
    if (!profileData?.org_id || profileData?.role_priv !== "org_admin") return;
    try {
      setAutopay(await fetchAutopayStatus(profileData.org_id));
    } catch (err) {
      console.error("AutoPay status failed", err);
      setAutopay(null);
    }
  };

  const openAutopaySetup = () => {
    const firstEligible = (autopay?.methods || []).find((m) => m.eligible);
    setAutopayValue({
      ...DEFAULT_AUTOPAY_VALUE,
      mode: "autopay",
      method: firstEligible?.method || null,
    });
    setAutopayModal(true);
  };

  const autopaySetupError = validateAutopaySelection(autopayValue, {
    methods: autopay?.methods || [],
  });

  const handleAutopaySetup = async () => {
    if (autopayBusy || autopaySetupError) return;
    setAutopayBusy(true);
    try {
      const result = await setupAutopayMandate({
        orgId: profileData.org_id,
        method: autopayValue.method,
        authType: autopayValue.authType,
        bankAccount: bankPayload(autopayValue.bank),
      });
      if (result.status === "active") {
        toast.success("AutoPay is on 🎉");
        setAutopayModal(false);
      } else if (result.status === "pending_confirmation") {
        toast("Submitted. Your bank will confirm within 2 working days.", {
          icon: "⏳",
        });
        setAutopayModal(false);
      } else if (result.status === "cancelled_by_user") {
        toast("AutoPay setup cancelled.");
      } else {
        toast.error("Your bank didn't approve AutoPay. Try another method.");
      }
    } catch (err) {
      toast.error(apiError(err, "AutoPay setup failed."));
    } finally {
      setAutopayBusy(false);
      loadAutopay();
    }
  };

  const handleAutopayCancel = async () => {
    if (autopayBusy) return;
    if (
      !window.confirm(
        "Turn off AutoPay? Future renewals will need to be paid manually before the expiry date.",
      )
    )
      return;
    setAutopayBusy(true);
    try {
      const res = await cancelAutopay(profileData.org_id);
      toast.success(
        res?.debitInFlight
          ? "AutoPay turned off. A renewal debit already sent to your bank will still complete."
          : "AutoPay turned off.",
      );
    } catch (err) {
      toast.error(apiError(err, "Couldn't turn off AutoPay."));
    } finally {
      setAutopayBusy(false);
      loadAutopay();
    }
  };

  const handleBuySeats = () => {
    navigate("/upgrade-seats");
  };

  const handleApplyPlanChange = async () => {
    if (!changeQuote || applying) return;

    // A downgrade whose credit fully covers the new cost needs no payment —
    // /payments/create-order/ applies it immediately and returns this
    // status instead of a Razorpay order. Otherwise, send them to Checkout
    // to actually pay the prorated amount.
    if (changeQuote.amount <= 0) {
      try {
        setApplying(true);
        const idToken = await auth.currentUser.getIdToken(true);
        await api.post(
          "/payments/create-order/",
          {
            plan_code: selectedPlan,
            billing_cycle: subscription.billingCycle,
            org_id: profileData.org_id,
            seats: Number(selectedSeats),
          },
          { headers: { Authorization: idToken } },
        );
        await fetchData();
        setChangeQuote(null);
      } catch (err) {
        console.error("Plan change failed", err);
      } finally {
        setApplying(false);
      }
      return;
    }

    navigate("/checkout", {
      state: {
        orgId: profileData.org_id,
        planCode: selectedPlan,
        billingCycle: subscription.billingCycle,
        seats: Number(selectedSeats),
      },
    });
  };

  const downloadInvoice = async (orderId) => {
    try {
      setDownloading(orderId);

      const user = auth.currentUser;
      if (!user) {
        console.error("User not logged in");
        return;
      }

      const token = await user.getIdToken();

      const res = await api.get(
        `/payments/invoice/${orderId}/?org_id=${profileData.org_id}`,
        {
          responseType: "blob",
          // method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // VALIDATION
      if (!(res.data instanceof Blob)) {
        console.error("Not a blob:", res.data);
        throw new Error("Invalid PDF response");
      }

      // const blob = await res.blob();
      const url = window.URL.createObjectURL(res.data);

      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice_${orderId}.pdf`;
      a.click();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Invoice download failed", err);

      // Debug helper
      if (err.response) {
        console.error("Response:", err.response.data);
      }
    } finally {
      setDownloading(null); // stop loading
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;

  // ── Expiry display ────────────────────────────────────────────────────────
  // /payments/current/ returns `expiresAt` as an ISO string (or, depending on
  // serialization, a Firestore-style { _seconds } / { seconds } object).
  const parseExpiry = (value) => {
    if (!value) return null;
    if (typeof value === "object") {
      const secs = value._seconds ?? value.seconds;
      if (secs != null) return new Date(secs * 1000);
    }
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  };

  const expiryDate = parseExpiry(subscription?.expiresAt);
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const cycleDays = subscription?.billingCycle === "yearly" ? 365 : 30;

  let expiry = null;
  if (expiryDate) {
    const msLeft = expiryDate.getTime() - Date.now();
    const daysLeft = Math.ceil(msLeft / MS_PER_DAY);
    const isExpired = subscription?.status === "expired" || msLeft <= 0;
    const isExpiringSoon = !isExpired && daysLeft <= 7;

    // Share of the current billing cycle that has already elapsed (0-100).
    const elapsedPct = isExpired
      ? 100
      : Math.min(
          100,
          Math.max(
            0,
            ((cycleDays * MS_PER_DAY - msLeft) / (cycleDays * MS_PER_DAY)) *
              100,
          ),
        );

    expiry = {
      formatted:
        expiryDate.toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
          timeZone: "Asia/Kolkata",
        }) + " IST",
      daysLeft,
      isExpired,
      isExpiringSoon,
      elapsedPct,
      label: isExpired
        ? "Expired"
        : isExpiringSoon
          ? "Expiring soon"
          : "Active",
      badge: isExpired
        ? "bg-red-100 text-red-700"
        : isExpiringSoon
          ? "bg-amber-100 text-amber-700"
          : "bg-green-100 text-green-700",
      bar: isExpired
        ? "bg-red-500"
        : isExpiringSoon
          ? "bg-amber-500"
          : "bg-green-500",
      daysText: isExpired
        ? `Expired ${Math.abs(daysLeft)} day${Math.abs(daysLeft) === 1 ? "" : "s"} ago`
        : daysLeft === 0
          ? "Expires today"
          : daysLeft === 1
            ? "Expires tomorrow"
            : `${daysLeft} days remaining`,
    };
  }

  const handleRenew = () => {
    navigate("/checkout", {
      state: {
        orgId: profileData.org_id,
        planCode: subscription.planCode,
        billingCycle: subscription.billingCycle,
        seats: Number(subscription?.seats?.paid) || 1,
      },
    });
  };

  return (
    <div className="p-5 space-y-5">
      <Toaster position="top-center" />
      {/* HERO CARD */}
      <div className="relative overflow-hidden rounded-[24px] p-5 bg-gradient-to-r from-indigo-500 via-purple-500 to-blue-500 text-white shadow-xl">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-sm opacity-80">Current Plan</h2>
            <h1 className="text-[28px] font-bold mt-1">
              {subscription?.planName}
            </h1>

            <p className="mt-2 text-lg">
              ₹{subscription?.price} / {subscription?.billingCycle}
            </p>

            <span
              className={`inline-block mt-2 px-3 py-1 text-xs rounded-full backdrop-blur ${darkMode ? "bg-[#1E293B]" : "bg-white"}`}
            >
              {subscription?.status}
            </span>
          </div>

          <div className="text-right">
            <p className="text-sm opacity-80">Available Seats</p>
            <p className="text-[34px] font-bold">
              {subscription?.seats?.available}
            </p>
          </div>
        </div>

        {/* glow effect */}
        <div
          className={`absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl ${darkMode ? "bg-[#1E293B]" : "bg-white"}`}
        ></div>
      </div>

      {/* SEAT CARDS */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        {[
          { label: "Total", value: subscription?.seats?.paid },
          { label: "Used", value: subscription?.seats?.used },
          { label: "Invited", value: subscription?.seats?.invited },
          {
            label: "Available",
            value: subscription?.seats?.available,
            highlight: true,
          },
        ].map((item, i) => (
          <div
            key={i}
            className={`p-4 rounded-2xl shadow-sm border hover:shadow-md transition ${darkMode ? "bg-[#1E293B]" : "bg-white"} ${
              item.highlight ? "border-green-400" : "border-gray-100"
            }`}
          >
            <p className="text-sm text-gray-500">{item.label}</p>
            <p
              className={`text-xl font-semibold ${
                item.highlight ? "text-green-600" : ""
              }`}
            >
              {item.value}
            </p>
          </div>
        ))}
      </div>

      {/* BUY SEATS  +  SUBSCRIPTION EXPIRY (second row, side by side) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* BUY SEATS */}
        <div
          className={`p-6 rounded-2xl shadow border ${darkMode ? "bg-[#1E293B] border-slate-700" : "bg-white"}`}
        >
          <h2 className="text-lg font-semibold mb-4">Add Seats</h2>

          <div className="flex items-center gap-4">
            <button
              onClick={handleBuySeats}
              className="bg-indigo-600 text-white px-4 py-2 rounded"
            >
              Buy Seats
            </button>
          </div>
        </div>

        {/* SUBSCRIPTION EXPIRY */}
        <div
          className={`p-6 rounded-2xl shadow border ${darkMode ? "bg-[#1E293B] border-slate-700" : "bg-white"}`}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Subscription Expiry</h2>
            {expiry && (
              <span
                className={`px-3 py-1 text-xs font-medium rounded-full ${expiry.badge}`}
              >
                {expiry.label}
              </span>
            )}
          </div>

          {!expiry ? (
            <p className={darkMode ? "text-slate-400" : "text-gray-500"}>
              Expiry date not available
            </p>
          ) : (
            <>
              <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                  <p
                    className={`text-sm ${darkMode ? "text-slate-400" : "text-gray-500"}`}
                  >
                    {expiry.isExpired ? "Expired on" : "Renews / expires on"}
                  </p>
                  <p className="text-xl font-semibold">{expiry.formatted}</p>
                </div>

                <p
                  className={`text-sm font-medium ${
                    expiry.isExpired
                      ? "text-red-600"
                      : expiry.isExpiringSoon
                        ? "text-amber-600"
                        : darkMode
                          ? "text-slate-300"
                          : "text-gray-700"
                  }`}
                >
                  {expiry.daysText}
                </p>
              </div>

              {/* Billing-cycle progress */}
              <div
                className={`mt-4 h-2 w-full rounded-full overflow-hidden ${darkMode ? "bg-slate-700" : "bg-gray-200"}`}
              >
                <div
                  className={`h-full rounded-full transition-all ${expiry.bar}`}
                  style={{ width: `${expiry.elapsedPct}%` }}
                />
              </div>

              {expiry.isExpired && (
                <button
                  onClick={handleRenew}
                  className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded"
                >
                  Renew Now
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* AUTOPAY */}
      {isOrgAdmin && autopay && (
        <div
          className={`p-6 rounded-2xl shadow border ${darkMode ? "bg-[#1E293B] border-slate-700" : "bg-white"}`}
        >
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <h2 className="text-lg font-semibold">AutoPay</h2>
            <span
              className={`px-3 py-1 text-xs font-medium rounded-full ${
                autopay.status === "active"
                  ? "bg-green-100 text-green-700"
                  : autopay.pendingMethod
                    ? "bg-blue-100 text-blue-700"
                    : [
                          "needs_action",
                          "needs_reauth",
                          "rejected",
                          "paused",
                        ].includes(autopay.status)
                      ? "bg-red-100 text-red-700"
                      : "bg-gray-100 text-gray-600"
              }`}
            >
              {autopay.status === "active"
                ? "On"
                : autopay.pendingMethod
                  ? "Waiting for bank"
                  : ["needs_action", "needs_reauth"].includes(autopay.status)
                    ? "Needs attention"
                    : autopay.status === "rejected"
                      ? "Not approved"
                      : autopay.status === "paused"
                        ? "Paused"
                        : "Off"}
            </span>
          </div>

          {autopay.status === "active" ? (
            <div
              className={`space-y-1 text-sm ${darkMode ? "text-slate-300" : "text-gray-700"}`}
            >
              <p>
                Paying with <span className="font-medium">{autopay.label}</span>
                {autopay.maxAmountRupees
                  ? ` · limit ₹${Number(autopay.maxAmountRupees).toLocaleString("en-IN")} per debit`
                  : ""}
              </p>
              {autopay.renewalAmountRupees != null && autopay.nextDebitOn && (
                <p>
                  Next debit of{" "}
                  <span className="font-medium">
                    ₹
                    {Number(autopay.renewalAmountRupees).toLocaleString(
                      "en-IN",
                    )}
                  </span>{" "}
                  around{" "}
                  {new Date(autopay.nextDebitOn).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                  . You'll get a pre-debit notice 24 hours before.
                </p>
              )}
              {autopay.debitInFlight && (
                <p className="text-blue-700">
                  A renewal debit is being processed by your bank.
                </p>
              )}
              {!autopay.coversRenewal &&
                autopay.renewalAmountRupees != null && (
                  <p className="text-amber-700">
                    Your renewal is above this AutoPay limit. Set up AutoPay
                    again to keep auto-renewing.
                  </p>
                )}
            </div>
          ) : (
            <p
              className={`text-sm ${darkMode ? "text-slate-400" : "text-gray-600"}`}
            >
              {autopay.statusReason ||
                (autopay.pendingMethod
                  ? "Your bank is confirming the mandate. This can take up to 2 working days."
                  : "Renew automatically with UPI AutoPay, a card, or a bank e-mandate. You can turn it off any time.")}
            </p>
          )}

          {autopay.lastCharge?.status === "retrying" && (
            <p className="text-sm text-red-600 mt-2">
              Last debit failed
              {autopay.lastCharge.error ? `: ${autopay.lastCharge.error}` : ""}.
              We'll retry automatically.
            </p>
          )}
          {autopay.lastCharge?.status === "failed" && (
            <p className="text-sm text-red-600 mt-2">
              AutoPay couldn't renew this period. Please renew manually.
            </p>
          )}

          <div className="flex gap-3 mt-4 flex-wrap">
            <button
              onClick={openAutopaySetup}
              disabled={autopayBusy || autopay.debitInFlight}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded disabled:opacity-60"
            >
              {autopay.status === "active" ? "Change method" : "Set up AutoPay"}
            </button>
            {autopay.status === "active" && (
              <button
                onClick={handleAutopayCancel}
                disabled={autopayBusy}
                className={`px-4 py-2 rounded border ${darkMode ? "border-slate-600" : "border-gray-300"} disabled:opacity-60`}
              >
                Turn off
              </button>
            )}
          </div>
        </div>
      )}

      {autopayModal && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Set up AutoPay"
        >
          <div
            className={`w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl p-6 shadow-xl ${darkMode ? "bg-[#1E293B] text-white" : "bg-white"}`}
          >
            <h3 className="text-lg font-semibold mb-3">Set up AutoPay</h3>
            <AutopayOptions
              options={{
                enabled: true,
                methods: autopay?.methods || [],
                renewalAmountRupees: autopay?.renewalAmountRupees,
              }}
              value={autopayValue}
              onChange={setAutopayValue}
              showModeToggle={false}
              disabled={autopayBusy}
              darkMode={darkMode}
            />
            {autopaySetupError && (
              <p className="text-xs text-amber-700 mt-2">{autopaySetupError}</p>
            )}
            <div className="flex gap-3 mt-5">
              <button
                onClick={handleAutopaySetup}
                disabled={autopayBusy || Boolean(autopaySetupError)}
                className="flex-1 bg-[#5E5ADB] hover:bg-[#4C47C7] text-white py-2 rounded-lg disabled:opacity-60"
              >
                {autopayBusy ? "Waiting for approval..." : "Continue"}
              </button>
              <button
                onClick={() => setAutopayModal(false)}
                disabled={autopayBusy}
                className={`px-4 py-2 rounded-lg border ${darkMode ? "border-slate-600" : "border-gray-300"}`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PAYMENT HISTORY */}
      <div
        className={` rounded-2xl shadow border overflow-hidden ${darkMode ? "bg-[#1E293B]" : "bg-white"}`}
      >
        <div className="p-5 border-b">
          <h2 className="text-lg font-semibold">Payment History</h2>
        </div>

        {history.length === 0 ? (
          <div
            className={`p-6 ${darkMode ? "text-slate-400" : "text-gray-500"}`}
          >
            No payments yet
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead
              className={
                darkMode
                  ? "bg-[#1E293B] text-slate-400"
                  : "bg-gray-50 text-gray-600"
              }
            >
              <tr>
                <th className="p-4 text-left">Order</th>
                <th className="p-4 text-left">Plan</th>
                <th className="p-4 text-left">Seats</th>
                <th className="p-4 text-left">Amount</th>
                <th className="p-4 text-left">Billing</th>
                <th className="p-4 text-left">Invoice</th>
              </tr>
            </thead>

            <tbody>
              {history.map((p) => (
                <tr
                  key={p.orderId}
                  className={`border-t transition ${darkMode ? "border-slate-700 hover:bg-[#1E293B]" : "hover:bg-gray-50"}`}
                >
                  <td className="p-4 font-medium">{p.orderId}</td>
                  <td className="p-4">{p.planName}</td>
                  <td className="p-4">{p.seats}</td>
                  <td className="p-4 font-semibold">₹{p.amount}</td>
                  <td className="p-4 capitalize">{p.billingCycle}</td>
                  <td className="p-4">
                    <button
                      onClick={() => downloadInvoice(p.orderId)}
                      disabled={downloading === p.orderId}
                      className={`px-3 py-1 rounded text-white ${
                        downloading === p.orderId
                          ? "bg-gray-400"
                          : "bg-indigo-500 hover:bg-indigo-600"
                      }`}
                    >
                      {downloading === p.orderId
                        ? "Downloading..."
                        : "Download"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Subscription;
